/**
 * A card arriving in your own hand, and how long it is worth looking at.
 *
 * Drawing a card used to be invisible from where you sat: the card flew to the
 * table's edge face down - it is nobody else's business - and then simply
 * appeared somewhere among the ones you already held, so finding it meant
 * reading your whole hand. What is missing is the beat a real draw has, where
 * the card is in your fingers before it joins the rest.
 *
 * ## Why a diff rather than a message
 *
 * `lastMove` carries `{ n, seat, move }` and not the card, because for anybody
 * else's draw the card is exactly what must not be sent. So the only side that
 * can say what you drew is yours, by noticing what is in your hand that was
 * not a moment ago. That limits the reveal to your OWN draws, which is the
 * only place it was ever wanted.
 */

/** Cards in `next` that were not in `prev`. */
export function arrivals(prev: readonly string[], next: readonly string[]): string[] {
  /*
   * Counted, not set-subtracted.
   *
   * A hand can legitimately hold two of the same card id in games dealt from
   * more than one deck, and `new Set(prev)` would swallow the second copy -
   * drawing your second ace of spades would arrive silently.
   */
  const held = new Map<string, number>();
  for (const card of prev) held.set(card, (held.get(card) ?? 0) + 1);

  const added: string[] = [];
  for (const card of next) {
    const left = held.get(card) ?? 0;
    if (left > 0) held.set(card, left - 1);
    else added.push(card);
  }
  return added;
}

/**
 * The card that just landed in a zone, when exactly one did.
 *
 * This is how a flight learns what it is carrying. `lastMove` says who moved
 * and which move it was, never the card - so for a play, the client finds the
 * card the same way it finds a draw: by looking at what is in the destination
 * that was not there a moment ago.
 *
 * `undefined` unless exactly one card arrived, and that is the whole safety
 * rule. A zone nobody can see returns no cards and so no answer; a trick that
 * cleared and refilled reports several and gets none. Both fall back to a card
 * back, which is what the table drew before any of this.
 */
export function landedCard(
  previous: readonly string[] | undefined,
  current: readonly string[] | undefined,
): string | undefined {
  const added = arrivals(previous ?? [], current ?? []);
  return added.length === 1 ? added[0] : undefined;
}

/**
 * How long the flown card takes to cross the table.
 *
 * Here rather than in the stylesheet because two things need it: the animation
 * itself, and the reveal that has to wait for the card to land before holding
 * it up. The stylesheet reads it from a custom property so there is one number
 * and not two that have to be kept equal by hand.
 */
export const FLIGHT_MS = 1400;

/**
 * How long the drawn card is held up before it joins the hand.
 *
 * Long enough to read a rank and a suit without thinking about it, short
 * enough that it is over before you have decided what to do - and a tap ends
 * it early, because in a game where you may play the card you just drew this
 * sits between a player and their turn.
 */
export const REVEAL_MS = 1100;

/**
 * How long the new card stays marked once it is in the hand.
 *
 * The reveal says what the card is; this says where it went. It is the half
 * that still works when several cards arrive at once and there is no single
 * card to hold up.
 */
export const FRESH_MS = 1500;
